import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImageUp, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserPhoto } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { getLocation, fmtCoord } from "@/lib/photo";

export const PhotoUploader = ({ className, testid }) => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const loc = await getLocation();
      const fd = new FormData();
      fd.append("file", file);
      if (loc) {
        fd.append("lat", loc.lat);
        fd.append("lng", loc.lng);
        fd.append("accuracy", loc.accuracy);
      }
      const r = await api.post("/me/photo", fd);
      setUser((u) => ({ ...u, ...r.data }));
      setOpen(false);
      if (loc) toast.success(`Foto disimpan • Tikor ${fmtCoord(loc)}`);
      else toast.warning("Foto disimpan, tetapi lokasi tidak didapat. Izinkan akses lokasi lalu unggah ulang.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} className="relative shrink-0 group" aria-label="Ganti foto">
        <UserPhoto user={user} className={className} />
        <span data-export-skip="1" className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-red-600 border-2 border-white flex items-center justify-center group-hover:scale-110 transition-transform">
          <Camera className="h-3 w-3 text-white" />
        </span>
      </button>
      <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="photo-camera-input" />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-gallery-input" />
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-sm" data-testid="photo-upload-dialog">
          <DialogHeader>
            <DialogTitle>Foto Anggota</DialogTitle>
            <DialogDescription>Titik koordinat (tikor) lokasi Anda akan dicatat otomatis saat foto diunggah.</DialogDescription>
          </DialogHeader>
          {busy ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-600" data-testid="photo-uploading">
              <Loader2 className="h-5 w-5 animate-spin text-red-600" /> Mengambil lokasi & mengunggah...
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => cameraRef.current.click()} data-testid="photo-open-camera" className="flex flex-col items-center gap-2 py-5 rounded-xl border border-slate-200 hover:bg-red-50 hover:border-red-300 transition-colors">
                <Camera className="h-7 w-7 text-red-600" /><span className="text-sm font-semibold">Ambil Foto</span>
              </button>
              <button onClick={() => galleryRef.current.click()} data-testid="photo-open-gallery" className="flex flex-col items-center gap-2 py-5 rounded-xl border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors">
                <ImageUp className="h-7 w-7 text-blue-800" /><span className="text-sm font-semibold">Pilih dari Galeri</span>
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
